import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Pencil } from "lucide-react";
import {
  ApiError,
  deleteClub,
  getSuperAdminClubDetail,
  getSuperAdminClubUsage,
  setClubStatus,
  updateSuperAdminClubFlags,
  updateSuperAdminClubMeta,
  updateSuperAdminClubQuotas,
} from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  bytesToGbInput,
  formatBytes,
  parseCount,
  parseGbToBytes,
  usagePercent,
  usageTone,
} from "@/features/super-admin/lib/usage";
import { unscopedKey } from "@/lib/queryClient";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/Button";
import { apexDomain } from "@/lib/hostKind";
import { ClubAdminsPanel } from "@/features/super-admin/components/ClubAdminsPanel";
import { EditClubDialog } from "@/features/super-admin/pages/ClubsPage";
import type { AdminClubDetail, ClubFlagKey, ClubOperatorMeta } from "@/lib/types";

function formatErr(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}

const FLAG_LABELS: Record<ClubFlagKey, { label: string; description: string }> = {
  downloads_enabled: {
    label: "Video downloads",
    description: "Members can download match video files.",
  },
  live_enabled: {
    label: "Live streaming",
    description: "RTMP ingest + live viewing for this club's teams.",
  },
  onboarding_self_service_enabled: {
    label: "Self-service onboarding",
    description: "People can register accounts without an invite.",
  },
  onboarding_social_login_enabled: {
    label: "Social login",
    description: "Master switch for the social sign-in buttons.",
  },
  onboarding_google_enabled: {
    label: "Google sign-in",
    description: "Offer Google as a sign-in option.",
  },
  onboarding_apple_enabled: {
    label: "Apple sign-in",
    description: "Offer Apple ID as a sign-in option.",
  },
};

// Stable render order: feature flags first, then the onboarding trio.
const FLAG_ORDER: ClubFlagKey[] = [
  "downloads_enabled",
  "live_enabled",
  "onboarding_self_service_enabled",
  "onboarding_social_login_enabled",
  "onboarding_google_enabled",
  "onboarding_apple_enabled",
];

const CARD = "flex flex-col gap-3 rounded-panel border border-rule bg-surface p-3";
const CARD_TITLE = "text-sm font-display uppercase tracking-wider text-ink";
const CARD_HINT = "text-[0.65rem] text-ink-muted";
const META_FIELD =
  "rounded-control border border-rule bg-field px-2 py-1.5 text-xs text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const META_LABEL = "text-[0.6rem] font-display uppercase tracking-wider text-ink-muted";

export function ClubDetailPage() {
  const { clubId } = useParams({ from: "/super-admin/clubs/$clubId" });
  const detailKey = unscopedKey("super-admin-club-detail", { clubId });
  const detailQuery = useQuery({
    queryKey: detailKey,
    queryFn: () => getSuperAdminClubDetail(clubId),
  });

  if (detailQuery.isPending) {
    return (
      <p className="text-xs font-mono text-ink-muted" data-testid="super-admin-club-detail-loading">
        Loading club…
      </p>
    );
  }
  if (detailQuery.isError) {
    return (
      <div className="flex flex-col gap-2" data-testid="super-admin-club-detail-error">
        <p className="text-destructive-text font-mono text-xs">{formatErr(detailQuery.error)}</p>
        <Link
          to="/super-admin/clubs"
          className="font-mono text-xs text-accent-text hover:underline"
        >
          Back to clubs
        </Link>
      </div>
    );
  }

  const detail = detailQuery.data;
  const club = detail.club;
  const suspended = club.status === "suspended";
  const archived = club.status === "archived";

  return (
    <div className="flex flex-col gap-3" data-testid="super-admin-club-detail">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <Link
            to="/super-admin/clubs"
            className="inline-flex items-center gap-1 font-mono text-[0.65rem] text-ink-muted hover:text-ink"
            data-testid="super-admin-club-detail-back"
          >
            <ArrowLeft size={12} aria-hidden /> All clubs
          </Link>
          <div className="flex items-center gap-2">
            <h2 className="truncate font-display text-base text-ink">{club.name}</h2>
            {suspended || archived ? (
              <span className="shrink-0 rounded-pill border border-rule bg-field px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
                {archived ? "Archived" : "Suspended"}
              </span>
            ) : null}
          </div>
          <p className="text-[0.6rem] font-mono text-ink-muted">
            {club.subdomain}.{apexDomain()}
          </p>
        </div>
        {!suspended ? (
          <a
            href={`https://${club.subdomain}.${apexDomain()}/admin/settings`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center gap-1 font-mono text-[0.65rem] text-accent-text hover:underline"
          >
            <ExternalLink size={12} aria-hidden /> Open club console
          </a>
        ) : null}
      </div>

      <IdentityCard detail={detail} detailKey={detailKey} />

      <section className={CARD} data-testid="super-admin-club-detail-admins">
        <header>
          <h3 className={CARD_TITLE}>Club admins</h3>
          <p className={CARD_HINT}>
            Seated admins, promotions, invites. A club always keeps at least one admin.
          </p>
        </header>
        <ClubAdminsPanel club={club} />
      </section>

      <FlagsCard detail={detail} clubId={clubId} detailKey={detailKey} />
      <OperatorMetaCard meta={detail.meta} clubId={clubId} detailKey={detailKey} />
      <UsageCard clubId={clubId} detailKey={detailKey} />
      <DangerZoneCard club={club} detailKey={detailKey} />
    </div>
  );
}

function DangerZoneCard({
  club,
  detailKey,
}: {
  club: AdminClubDetail["club"];
  detailKey: readonly unknown[];
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const archived = club.status === "archived";
  const suspended = club.status === "suspended";

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: detailKey }),
      queryClient.invalidateQueries({ queryKey: unscopedKey("super-admin-clubs") }),
    ]);

  const statusMut = useMutation({
    mutationFn: (next: "active" | "suspended" | "archived") =>
      setClubStatus(club.id, next),
    onSuccess: async (_club, next) => {
      toast.success(
        next === "archived" ? `${club.name} archived` : `${club.name} restored`,
      );
      await invalidate();
    },
    onError: (err) =>
      toast.error("Couldn't update club", { description: formatErr(err) }),
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteClub(club.id, confirmName.trim()),
    onSuccess: () => {
      toast.success(`${club.name} deleted forever`);
      void queryClient.invalidateQueries({ queryKey: unscopedKey("super-admin-clubs") });
      void navigate({ to: "/super-admin/clubs", replace: true });
    },
    onError: (err) => {
      const detail =
        err instanceof ApiError && typeof err.detail === "string" ? err.detail : "";
      setDeleteError(
        detail === "club_not_archived"
          ? "Only archived clubs can be deleted. Archive the club first."
          : detail === "confirm_name_mismatch"
            ? "The name doesn't match. Type the club's exact name."
            : formatErr(err),
      );
    },
  });

  return (
    <section
      className="flex flex-col gap-3 rounded-panel border border-destructive/40 bg-surface p-3"
      data-testid="super-admin-club-danger"
    >
      <header>
        <h3 className={CARD_TITLE}>Danger zone</h3>
        <p className={CARD_HINT}>
          Lifecycle: suspend (host off, reversible) → archive (retired: hidden
          from the default list, invites refused, reversible) → delete
          (forever). Delete is only offered on an archived club.
        </p>
      </header>
      <div className="flex flex-wrap items-center gap-2">
        {suspended ? (
          <Button
            variant="outline"
            size="sm"
            disabled={statusMut.isPending}
            onClick={() => statusMut.mutate("archived")}
            data-testid="super-admin-club-archive"
          >
            Archive club
          </Button>
        ) : null}
        {archived ? (
          <Button
            variant="outline"
            size="sm"
            disabled={statusMut.isPending}
            onClick={() => statusMut.mutate("active")}
            data-testid="super-admin-club-restore"
          >
            Restore club
          </Button>
        ) : null}
        <Button
          variant="flag"
          size="sm"
          disabled={!archived}
          title={archived ? undefined : "Archive the club first"}
          onClick={() => {
            setConfirmName("");
            setDeleteError(null);
            setDeleteOpen(true);
          }}
          data-testid="super-admin-club-delete"
        >
          Delete club forever
        </Button>
        {!archived ? (
          <span className="font-mono text-[0.6rem] text-ink-muted">
            {suspended
              ? "Archive the club to unlock delete."
              : "Suspend, then archive, to unlock delete."}
          </span>
        ) : null}
      </div>

      {deleteOpen ? (
        <ConfirmDialog
          title={`Delete ${club.name} forever?`}
          confirmLabel={deleteMut.isPending ? "Deleting…" : "Delete club forever"}
          confirmDisabled={deleteMut.isPending || confirmName.trim() !== club.name}
          danger
          onConfirm={() => deleteMut.mutate()}
          onClose={() => setDeleteOpen(false)}
          message={
            <div className="flex flex-col gap-3 text-xs">
              <p className="rounded-control border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive-text">
                This permanently removes the club — every team, match, video,
                coaching item, membership, invite, and its operator metadata.
                Media files are wiped from disk. This cannot be undone.
              </p>
              <label className="flex flex-col gap-1 font-mono text-ink">
                <span className="text-ink-muted">
                  Type the club name exactly to confirm:{" "}
                  <strong className="text-ink">{club.name}</strong>
                </span>
                <input
                  type="text"
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  autoComplete="off"
                  data-testid="super-admin-club-delete-confirm-name"
                  className={META_FIELD}
                />
              </label>
              {deleteError ? (
                <p
                  data-testid="super-admin-club-delete-error"
                  className="font-mono text-destructive-text"
                >
                  {deleteError}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </section>
  );
}

function IdentityCard({
  detail,
  detailKey,
}: {
  detail: AdminClubDetail;
  detailKey: readonly unknown[];
}) {
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const club = detail.club;
  return (
    <section className={CARD} data-testid="super-admin-club-identity">
      <div className="flex items-start justify-between gap-2">
        <header>
          <h3 className={CARD_TITLE}>Identity</h3>
          <p className={CARD_HINT}>
            Name, slug, and the subdomain the club lives on.
          </p>
        </header>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setEditOpen(true)}
          data-testid="super-admin-club-identity-edit"
        >
          <Pencil size={13} aria-hidden /> Edit
        </Button>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
        <dt className={META_LABEL}>Name</dt>
        <dd className="text-ink">{club.name}</dd>
        <dt className={META_LABEL}>Slug</dt>
        <dd className="font-mono text-ink">{club.slug}</dd>
        <dt className={META_LABEL}>Subdomain</dt>
        <dd className="font-mono text-ink">
          {club.subdomain}.{apexDomain()}
        </dd>
      </dl>
      {editOpen ? (
        <EditClubDialog
          club={club}
          onClose={() => {
            setEditOpen(false);
            void queryClient.invalidateQueries({ queryKey: detailKey });
          }}
        />
      ) : null}
    </section>
  );
}

function FlagsCard({
  detail,
  clubId,
  detailKey,
}: {
  detail: AdminClubDetail;
  clubId: string;
  detailKey: readonly unknown[];
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const mut = useMutation({
    mutationFn: (change: { key: ClubFlagKey; next: boolean }) =>
      updateSuperAdminClubFlags(clubId, { [change.key]: change.next }),
    onSuccess: (_data, change) => {
      toast.success(
        `${FLAG_LABELS[change.key].label} ${change.next ? "enabled" : "disabled"}`,
      );
      void queryClient.invalidateQueries({ queryKey: detailKey });
    },
    onError: (err) =>
      toast.error("Couldn't update flag", { description: formatErr(err) }),
  });

  return (
    <section className={CARD} data-testid="super-admin-club-flags">
      <header>
        <h3 className={CARD_TITLE}>Feature flags</h3>
        <p className={CARD_HINT}>
          Operator-controlled behavior for this club. Branding stays with the
          club's own admins. "Inherited" follows the platform default until
          the club gets its own override.
        </p>
      </header>
      <ul className="flex flex-col gap-2">
        {FLAG_ORDER.map((key) => {
          const enabled = detail.flags[key] === "1";
          const overridden = detail.flags_overridden.includes(key);
          return (
            <li key={key} className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-ink">{FLAG_LABELS[key].label}</span>
                  <span
                    className="rounded-pill border border-rule bg-field px-1.5 py-0.5 text-[0.5rem] font-display uppercase tracking-wider text-ink-muted"
                    data-testid={`super-admin-club-flag-${key}-source`}
                  >
                    {overridden ? "Club override" : "Inherited"}
                  </span>
                </div>
                <p className="text-[0.6rem] leading-snug text-ink-muted">
                  {FLAG_LABELS[key].description}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                aria-label={FLAG_LABELS[key].label}
                disabled={mut.isPending}
                onClick={() => mut.mutate({ key, next: !enabled })}
                data-testid={`super-admin-club-flag-${key}`}
                data-checked={enabled}
                className={[
                  "relative h-5 w-9 shrink-0 rounded-full border transition-colors",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  enabled ? "border-accent bg-accent" : "border-rule bg-field",
                ].join(" ")}
              >
                <span
                  aria-hidden="true"
                  className={[
                    "absolute top-0.5 h-3.5 w-3.5 rounded-full transition-all",
                    enabled ? "left-[1.15rem] bg-paper" : "left-0.5 bg-ink",
                  ].join(" ")}
                />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

type MetaDraft = Pick<
  ClubOperatorMeta,
  "contact_name" | "contact_email" | "plan_label" | "operator_notes"
>;

function OperatorMetaCard({
  meta,
  clubId,
  detailKey,
}: {
  meta: ClubOperatorMeta;
  clubId: string;
  detailKey: readonly unknown[];
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<MetaDraft>({
    contact_name: meta.contact_name,
    contact_email: meta.contact_email,
    plan_label: meta.plan_label,
    operator_notes: meta.operator_notes,
  });
  // Re-sync the draft when a fresh detail payload arrives (e.g. after save).
  useEffect(() => {
    setDraft({
      contact_name: meta.contact_name,
      contact_email: meta.contact_email,
      plan_label: meta.plan_label,
      operator_notes: meta.operator_notes,
    });
  }, [meta.contact_name, meta.contact_email, meta.plan_label, meta.operator_notes]);

  const dirtyKeys = (Object.keys(draft) as Array<keyof MetaDraft>).filter(
    (key) => (draft[key] ?? "") !== (meta[key] ?? ""),
  );

  const mut = useMutation({
    mutationFn: () =>
      updateSuperAdminClubMeta(
        clubId,
        Object.fromEntries(dirtyKeys.map((key) => [key, draft[key] ?? ""])),
      ),
    onSuccess: () => {
      toast.success("Operator notes saved");
      void queryClient.invalidateQueries({ queryKey: detailKey });
    },
    onError: (err) =>
      toast.error("Couldn't save operator metadata", { description: formatErr(err) }),
  });

  const setField = (key: keyof MetaDraft) => (value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  return (
    <section className={CARD} data-testid="super-admin-club-meta">
      <header>
        <h3 className={CARD_TITLE}>Operator metadata</h3>
        <p className={CARD_HINT}>
          Operator-only bookkeeping — never shown to the club.
        </p>
      </header>
      <div className="grid gap-2 sm:grid-cols-3">
        <label className="flex flex-col gap-1">
          <span className={META_LABEL}>Contact name</span>
          <input
            className={META_FIELD}
            value={draft.contact_name ?? ""}
            onChange={(e) => setField("contact_name")(e.target.value)}
            data-testid="super-admin-club-meta-contact-name"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className={META_LABEL}>Contact email</span>
          <input
            type="email"
            className={META_FIELD}
            value={draft.contact_email ?? ""}
            onChange={(e) => setField("contact_email")(e.target.value)}
            data-testid="super-admin-club-meta-contact-email"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className={META_LABEL}>Plan label</span>
          <input
            className={META_FIELD}
            value={draft.plan_label ?? ""}
            onChange={(e) => setField("plan_label")(e.target.value)}
            placeholder="trial / standard / …"
            data-testid="super-admin-club-meta-plan"
          />
        </label>
      </div>
      <label className="flex flex-col gap-1">
        <span className={META_LABEL}>Internal notes</span>
        <textarea
          className={`${META_FIELD} min-h-20 resize-y`}
          value={draft.operator_notes ?? ""}
          onChange={(e) => setField("operator_notes")(e.target.value)}
          data-testid="super-admin-club-meta-notes"
        />
      </label>
      <div className="flex items-center justify-between gap-2">
        <p className="font-mono text-[0.6rem] text-ink-muted">
          {meta.updated_at
            ? `Last updated ${meta.updated_at}${meta.updated_by ? ` by ${meta.updated_by}` : ""}`
            : "Nothing saved yet."}
        </p>
        <Button
          size="sm"
          disabled={mut.isPending || dirtyKeys.length === 0}
          onClick={() => mut.mutate()}
          data-testid="super-admin-club-meta-save"
        >
          {mut.isPending ? "Saving…" : "Save"}
        </Button>
      </div>
    </section>
  );
}

const TONE_CLASSES: Record<ReturnType<typeof usageTone>, string> = {
  ok: "bg-accent",
  warn: "bg-flag/70",
  over: "bg-destructive",
};

function UsageBar({
  label,
  usedLabel,
  percent,
  testid,
}: {
  label: string;
  usedLabel: string;
  percent: number | null;
  testid: string;
}) {
  const tone = usageTone(percent);
  return (
    <div data-testid={testid} data-tone={tone}>
      <div className="flex items-baseline justify-between gap-2">
        <span className={META_LABEL}>{label}</span>
        <span className="font-mono text-[0.65rem] text-ink">{usedLabel}</span>
      </div>
      {percent === null ? (
        <p className="mt-1 font-mono text-[0.6rem] text-ink-muted">No limit set.</p>
      ) : (
        <div className="mt-1 h-1.5 overflow-hidden rounded-full border border-rule/60 bg-field">
          <div
            className={`h-full rounded-full transition-all ${TONE_CLASSES[tone]}`}
            style={{ width: `${Math.min(100, percent)}%` }}
          />
        </div>
      )}
    </div>
  );
}

function UsageCard({
  clubId,
  detailKey,
}: {
  clubId: string;
  detailKey: readonly unknown[];
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const usageKey = unscopedKey("super-admin-club-usage", { clubId });
  const usageQuery = useQuery({
    queryKey: usageKey,
    queryFn: () => getSuperAdminClubUsage(clubId),
  });
  const usage = usageQuery.data;

  const [editing, setEditing] = useState(false);
  const [storageGb, setStorageGb] = useState("");
  const [maxTeams, setMaxTeams] = useState("");
  const [maxMembers, setMaxMembers] = useState("");

  const beginEdit = () => {
    if (!usage) return;
    setStorageGb(bytesToGbInput(usage.storage.quota_bytes));
    setMaxTeams(usage.teams.max === null ? "" : String(usage.teams.max));
    setMaxMembers(usage.members.max === null ? "" : String(usage.members.max));
    setEditing(true);
  };

  const parsedStorage = parseGbToBytes(storageGb);
  const parsedTeams = parseCount(maxTeams);
  const parsedMembers = parseCount(maxMembers);
  // A malformed entry must block the save — silently coercing it to
  // "unlimited" would let a mistyped tightening quietly remove the limit.
  const invalid = !parsedStorage.ok || !parsedTeams.ok || !parsedMembers.ok;

  const mut = useMutation({
    mutationFn: () => {
      if (!parsedStorage.ok || !parsedTeams.ok || !parsedMembers.ok) {
        throw new Error(
          "Enter a non-negative number, or leave a field blank for unlimited.",
        );
      }
      return updateSuperAdminClubQuotas(clubId, {
        storage_quota_bytes: parsedStorage.value,
        max_teams: parsedTeams.value,
        max_members: parsedMembers.value,
      });
    },
    onSuccess: () => {
      toast.success("Quotas saved");
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: usageKey });
      void queryClient.invalidateQueries({ queryKey: detailKey });
    },
    onError: (err) =>
      toast.error("Couldn't save quotas", { description: formatErr(err) }),
  });

  return (
    <section className={CARD} data-testid="super-admin-club-usage">
      <div className="flex items-start justify-between gap-2">
        <header>
          <h3 className={CARD_TITLE}>Usage &amp; quotas</h3>
          <p className={CARD_HINT}>
            Storage is enforced at upload time; team/member limits only warn.
            Blank = unlimited.
          </p>
        </header>
        {!editing ? (
          <Button
            variant="outline"
            size="sm"
            onClick={beginEdit}
            disabled={!usage}
            data-testid="super-admin-club-quota-edit"
          >
            Edit quotas
          </Button>
        ) : null}
      </div>

      {usageQuery.isPending ? (
        <p className="font-mono text-[0.65rem] text-ink-muted">Measuring usage…</p>
      ) : usageQuery.isError ? (
        <p className="font-mono text-xs text-destructive-text">
          {formatErr(usageQuery.error)}
        </p>
      ) : usage ? (
        <div className="flex flex-col gap-3">
          <UsageBar
            label="Storage"
            usedLabel={
              usage.storage.quota_bytes === null
                ? `${formatBytes(usage.storage.used_bytes)} used`
                : `${formatBytes(usage.storage.used_bytes)} of ${formatBytes(usage.storage.quota_bytes)}`
            }
            percent={usagePercent(usage.storage.used_bytes, usage.storage.quota_bytes)}
            testid="super-admin-club-usage-storage"
          />
          <UsageBar
            label="Teams"
            usedLabel={
              usage.teams.max === null
                ? `${usage.teams.count}`
                : `${usage.teams.count} of ${usage.teams.max}`
            }
            percent={usagePercent(usage.teams.count, usage.teams.max)}
            testid="super-admin-club-usage-teams"
          />
          <UsageBar
            label="Members"
            usedLabel={
              usage.members.max === null
                ? `${usage.members.count}`
                : `${usage.members.count} of ${usage.members.max}`
            }
            percent={usagePercent(usage.members.count, usage.members.max)}
            testid="super-admin-club-usage-members"
          />
        </div>
      ) : null}

      {editing ? (
        <div className="flex flex-col gap-2 border-t border-rule pt-3">
          <div className="grid gap-2 sm:grid-cols-3">
            <label className="flex flex-col gap-1">
              <span className={META_LABEL}>Storage (GB)</span>
              <input
                inputMode="decimal"
                className={META_FIELD}
                value={storageGb}
                onChange={(e) => setStorageGb(e.target.value)}
                placeholder="unlimited"
                data-testid="super-admin-club-quota-storage"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className={META_LABEL}>Max teams</span>
              <input
                inputMode="numeric"
                className={META_FIELD}
                value={maxTeams}
                onChange={(e) => setMaxTeams(e.target.value)}
                placeholder="unlimited"
                data-testid="super-admin-club-quota-teams"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className={META_LABEL}>Max members</span>
              <input
                inputMode="numeric"
                className={META_FIELD}
                value={maxMembers}
                onChange={(e) => setMaxMembers(e.target.value)}
                placeholder="unlimited"
                data-testid="super-admin-club-quota-members"
              />
            </label>
          </div>
          {invalid ? (
            <p
              className="font-mono text-[0.65rem] text-destructive-text"
              data-testid="super-admin-club-quota-error"
            >
              Enter a non-negative number, or leave a field blank for unlimited.
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={mut.isPending || invalid}
              onClick={() => mut.mutate()}
              data-testid="super-admin-club-quota-save"
            >
              {mut.isPending ? "Saving…" : "Save quotas"}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
